import React from 'react';
import { styled } from '@linaria/react';

import useBoundingBox from '@/hooks/use-bounding-box';
import {
  clamp,
  convertCartesianToPolar,
  convertRadiansToDegrees,
  roundToNearest,
} from '@/utils';

import ControlWrapper from '@/components/Controls/ControlWrapper';
import ValuePreview from '@/components/Controls/ValuePreview';
import AngleBackground from './AngleBackground';

const OUTER_BORDER_WIDTH = 3;

interface Props {
  id: string;
  angle: number;
  precision?: number;
  setAngle: (angle: number) => void;
}

function AngleControl({ id, angle, precision = 5, setAngle }: Props) {
  const [isDragging, setIsDragging] = React.useState(false);

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const box = useBoundingBox(wrapperRef);

  React.useEffect(() => {
    if (!isDragging) {
      return;
    }

    function handleMouseUp() {
      setIsDragging(false);
    }

    function handleMouseMove(ev: MouseEvent | TouchEvent) {
      if (!box) {
        return;
      }

      const newAngle = deriveAngleFromMousePosition(
        ev,
        box,
        precision
      );
      setAngle(newAngle);
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('touchmove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, box, precision, setAngle]);

  function handleStartDragging(
    ev: React.MouseEvent | React.TouchEvent
  ) {
    if (!box) {
      return;
    }

    ev.preventDefault();
    setIsDragging(true);

    const newAngle = deriveAngleFromMousePosition(ev, box, precision);
    setAngle(newAngle);
  }

  function handleKeyDown(ev: React.KeyboardEvent) {
    if (!ev.key.match(/^Arrow/)) {
      return;
    }

    ev.preventDefault();

    const newAngle = deriveAngleFromKeyArrow(ev.key, angle);

    if (typeof newAngle === 'number') {
      setAngle(newAngle);
    }
  }

  return (
    <ControlWrapper
      id={id}
      label="Angle"
      labelAs="span"
      cornerContents={
        <ValuePreview
          value={angle}
          renderer={(value) => `${value}deg`}
        />
      }
    >
      <Wrapper
        ref={wrapperRef}
        onMouseDown={handleStartDragging}
        onTouchStart={handleStartDragging}
      >
        <Background>
          {box && (
            <AngleBackground
              width={box.width}
              height={box.width}
              borderWidth={OUTER_BORDER_WIDTH}
            />
          )}
        </Background>
        <VisualizationWrapper>
          <Svg viewBox="0 0 200 200">
            <circle />
          </Svg>
        </VisualizationWrapper>
        <NeedleWrapper style={{ '--rotation': angle + 'deg' }}>
          <Needle onKeyDown={handleKeyDown} />
        </NeedleWrapper>

        <NeedleCap />
      </Wrapper>
    </ControlWrapper>
  );
}

function deriveAngleFromMousePosition(
  ev: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent,
  box: DOMRect,
  precision: number
) {
  let x, y;

  const isTouchEvent = 'touches' in ev;
  if (isTouchEvent) {
    const touch = ev.touches[0];
    [x, y] = [touch.clientX, touch.clientY];
  } else {
    [x, y] = [ev.clientX, ev.clientY];
  }

  const relativeX = x - box.left;
  const relativeY = y - box.top;

  const centerX = box.width / 2;
  const centerY = box.height / 2;

  const [theta] = convertCartesianToPolar(
    [relativeX, relativeY],
    [centerX, centerY]
  );

  let angle = convertRadiansToDegrees(theta);

  // By default, our resulting angle is 90-degrees off.
  angle = (angle + 90) % 360;

  // In order to make it easier to select typical values like 90
  // degrees, we'll round to a given increment. By default, 5.
  // This means you can select 85, 90, 95. But not 89 or 91.
  angle = roundToNearest(angle, precision);

  // Avoid haviung both 360 and 0.
  if (angle === 360) {
    angle = 0;
  }

  return angle;
}

function deriveAngleFromKeyArrow(arrow: string, angle: number) {
  switch (arrow) {
    case 'ArrowUp': {
      // Ignore when we're at the extremes
      if (angle === 0) {
        return angle;
      } else if (angle >= 355) {
        return 0;
      }

      return angle < 180
        ? clamp(angle - 5, 0, 180)
        : clamp(angle + 5, 180, 355);
    }

    case 'ArrowDown': {
      if (angle === 180) {
        return angle;
      }

      return angle < 180
        ? clamp(angle + 5, 0, 180)
        : clamp(angle - 5, 180, 355);
    }
    case 'ArrowLeft': {
      if (angle === 270) {
        return angle;
      }
      if (angle < 5) {
        return angle + 355;
      }

      if (angle >= 0 && angle < 90) {
        return clamp(angle - 5, 0, 90);
      } else if (angle >= 90 && angle < 270) {
        return clamp(angle + 5, 90, 270);
      } else {
        return clamp(angle - 5, 270, 360);
      }
    }
    case 'ArrowRight': {
      if (angle === 90) {
        return angle;
      }
      if (angle >= 355) {
        return angle - 355;
      }

      if (angle >= 0 && angle < 90) {
        return clamp(angle + 5, 0, 90);
      } else if (angle >= 90 && angle < 270) {
        return clamp(angle - 5, 90, 270);
      } else {
        return clamp(angle + 5, 270, 360);
      }
    }
  }
}

const Wrapper = styled.div`
  position: relative;
  touch-action: none;
  /*
    When dragging the angle, the box rotates.
    This can lead to a scrollburglar, because the corners of the
    box peek out beyond the container.
  */
  overflow: hidden;
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const VisualizationWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
  vector-effect: non-scaling-stroke;
`;

const NeedleWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transform: rotate(var(--rotation));
  transform-origin: center center;
`;

const Needle = styled.button`
  position: absolute;
  top: 5%;
  left: 0;
  right: 0;
  width: 5px;
  height: 60%;
  margin-left: auto;
  margin-right: auto;
  background: white;
  border-radius: 1000px;

  &:focus-visible {
    outline-offset: 4px;
    outline-color: white;
  }
`;

const NeedleCap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 24px;
  height: 24px;
  margin: auto;
  border-radius: 50%;
  background: var(--color-gray-300);
  border: 2px solid var(--color-background);
`;

export default React.memo(AngleControl);
